import { CircularProgress, styled, Tab, Tabs, Typography } from '@mui/material';
import Spacing from 'components/Spacing';
import useKyc from 'hooks/data/kyc/useKyc';
import { FC, useEffect, useState } from 'react';
import AmlDetailItem from './AmlDetailItem';
import Heading from 'components/Heading';

type Props = {
  caDocId: string;
  investorId: string;
};

const StyledContainer = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(2),
  padding: theme.spacing(5),
  borderRadius: '4px',
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

enum AmlTabs {
  Pep,
  FitnessProbity,
  Warning,
  AdverseMedia,
}

const AmlResultInformation: FC<Props> = ({ caDocId, investorId }) => {
  const { amlResultInformation } = useKyc(investorId, caDocId);

  const amlHit = amlResultInformation.data;

  const [activeTab, setActiveTab] = useState<AmlTabs>(AmlTabs.Pep);

  const handleTabChange = (_: unknown, selectedTab: AmlTabs) => {
    setActiveTab(selectedTab);
  };

  const showPepTab = amlHit?.pepDetails.length > 0;
  const showFitnessProbityTab = amlHit?.fitnessProbityDetails.length > 0;
  const showWarningTab = amlHit?.warningDetails.length > 0;
  const showAdverseMediaTab = amlHit?.adverseMediaDetails.length > 0;

  useEffect(() => {
    if (showPepTab) {
      setActiveTab(AmlTabs.Pep);
    } else if (showFitnessProbityTab) {
      setActiveTab(AmlTabs.FitnessProbity);
    } else if (showWarningTab) {
      setActiveTab(AmlTabs.Warning);
    } else if (showAdverseMediaTab) {
      setActiveTab(AmlTabs.AdverseMedia);
    }
  }, [amlHit]);

  if (amlResultInformation.isPending) {
    return <CircularProgress size={20} />;
  }

  return (
    <StyledContainer>
      <StyledTitle variant="h4">Result information</StyledTitle>
      <Spacing size="sm" fullWidth>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          showDivider
          variant="scrollable"
        >
          {showPepTab && (
            <Tab
              value={AmlTabs.Pep}
              label={
                <Heading variant="h5" noMargin>
                  PEP
                </Heading>
              }
            />
          )}
          {showFitnessProbityTab && (
            <Tab
              value={AmlTabs.FitnessProbity}
              label={
                <Heading variant="h5" noMargin>
                  Fitness Probity
                </Heading>
              }
            />
          )}
          {showWarningTab && (
            <Tab
              value={AmlTabs.Warning}
              label={
                <Heading variant="h5" noMargin>
                  Warning
                </Heading>
              }
            />
          )}
          {showAdverseMediaTab && (
            <Tab
              value={AmlTabs.AdverseMedia}
              label={
                <Heading variant="h5" noMargin>
                  Adverse Media
                </Heading>
              }
            />
          )}
        </Tabs>
        {activeTab === AmlTabs.Pep &&
          amlHit?.pepDetails.map((entity, index) => (
            <AmlDetailItem
              key={`pep-${index}`}
              entity={entity}
              index={index}
              type="pep"
            />
          ))}

        {activeTab === AmlTabs.FitnessProbity &&
          amlHit?.fitnessProbityDetails.map((entity, index) => (
            <AmlDetailItem
              key={`fitness-${index}`}
              entity={entity}
              index={index}
              type="fitness"
            />
          ))}

        {activeTab === AmlTabs.Warning &&
          amlHit?.warningDetails.map((entity, index) => (
            <AmlDetailItem
              key={`warning-${index}`}
              entity={entity}
              index={index}
              type="warning"
            />
          ))}

        {activeTab === AmlTabs.AdverseMedia &&
          amlHit?.adverseMediaDetails.map((entity, index) => (
            <AmlDetailItem
              key={`adverse-media-${index}`}
              entity={entity}
              index={index}
              type="adverse-media"
            />
          ))}
      </Spacing>
    </StyledContainer>
  );
};

export default AmlResultInformation;
